/**
 * CLV Trends Panel (clv_trends, admin-only while it is being tested)
 *
 * Displays closing line value trends over time.
 * Shows weekly CLV averages and beat-close percentage.
 */

import { useClvTrends } from '@hooks/useClvTrends';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { TrendingUp } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';

export function ClvTrendsPanel(): ReactElement | null {
  const { isHidden } = useFeatureAccess();
  const { trends, loading, loaded, error, load } = useClvTrends();
  const [isOpen, setIsOpen] = useState(false);

  const hidden = isHidden('clv_trends');

  // Once per page: a failed load is not retried in a loop.
  useEffect(() => {
    if (isOpen && !loaded && !loading && !error && !hidden) load();
  }, [isOpen, loaded, loading, error, hidden, load]);

  if (hidden) return null;

  return (
    <div className="border border-theme-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="w-full flex items-center justify-between p-3 bg-theme-surface hover:bg-theme-surface-hover transition text-left"
      >
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-green-400" />
          <span className="text-sm font-medium text-white">CLV Trends</span>
        </div>
        <span className="text-xs text-theme-text-tertiary">{isOpen ? 'Hide' : 'Show'}</span>
      </button>

      {isOpen && (
        <div className="p-3 border-t border-theme-border">
          {loading ? (
            <p className="text-xs text-theme-text-tertiary animate-pulse">Loading CLV data...</p>
          ) : trends.length === 0 ? (
            <p className="text-xs text-theme-text-tertiary">
              No CLV data yet. Data accumulates as games are settled with closing lines captured.
            </p>
          ) : (
            <div className="space-y-2">
              {/* Summary stats */}
              <div className="flex gap-4 text-xs">
                <div>
                  <span className="text-theme-text-tertiary">Avg CLV: </span>
                  <span
                    className={
                      trends.reduce((s, t) => s + t.avgClv * t.pickCount, 0) /
                        trends.reduce((s, t) => s + t.pickCount, 0) >
                      0
                        ? 'text-green-400'
                        : 'text-red-400'
                    }
                  >
                    {(
                      trends.reduce((s, t) => s + t.avgClv * t.pickCount, 0) /
                      Math.max(
                        1,
                        trends.reduce((s, t) => s + t.pickCount, 0),
                      )
                    ).toFixed(2)}{' '}
                    pts
                  </span>
                </div>
                <div>
                  <span className="text-theme-text-tertiary">Beat Close: </span>
                  <span className="text-white">
                    {(
                      trends.reduce((s, t) => s + t.beatClosePct * t.pickCount, 0) /
                      Math.max(
                        1,
                        trends.reduce((s, t) => s + t.pickCount, 0),
                      )
                    ).toFixed(1)}
                    %
                  </span>
                </div>
                <div>
                  <span className="text-theme-text-tertiary">Weeks: </span>
                  <span className="text-white">{trends.length}</span>
                </div>
              </div>

              {/* Week-by-week bars */}
              <div className="space-y-1">
                {trends.slice(-8).map((t) => (
                  <div key={t.week} className="flex items-center gap-2 text-[10px]">
                    <span className="text-theme-text-tertiary w-16 shrink-0">
                      {t.week.slice(5, 10)}
                    </span>
                    <div className="flex-1 h-3 bg-theme-surface rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${t.avgClv > 0 ? 'bg-green-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.min(100, Math.abs(t.avgClv) * 20 + 10)}%` }}
                      />
                    </div>
                    <span
                      className={`w-12 text-right ${t.avgClv > 0 ? 'text-green-400' : 'text-red-400'}`}
                    >
                      {t.avgClv > 0 ? '+' : ''}
                      {t.avgClv.toFixed(1)}
                    </span>
                    <span className="text-theme-text-tertiary w-8 text-right">{t.pickCount}p</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
